import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { auth } from "@/lib/firebase";
import { Logo } from "@/components/shared/Logo";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { CheckCircleIcon } from "@heroicons/react/24/solid";

export function LoginSuccess() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = auth.currentUser;

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container flex items-center justify-center py-8">
        <div className="w-full max-w-md space-y-8">
          <Logo />
          <Card className="w-full shadow-lg border-primary/10">
            <CardHeader className="space-y-1">
              <div className="flex items-center justify-center mb-4">
                <CheckCircleIcon className="h-16 w-16 text-success" />
              </div>
              <CardTitle className="text-2xl font-bold text-center text-primary">
                {t("loginSuccess.title") || "Login Successful!"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="border-primary/20 bg-primary/5">
                <AlertDescription>
                  <p className="text-center">
                    {t("loginSuccess.message") || "You are now logged in and ready to use GreenAR."}
                  </p>
                </AlertDescription>
              </Alert>

              <div className="space-y-4 text-center">
                <p className="text-sm text-muted-foreground">
                  {t("loginSuccess.instructions") || "Open the GreenAR mobile app to get started."}
                </p>

                <div className="pt-4 space-y-3">
                  <Button
                    onClick={() => {
                      // Try to open mobile app via deep link
                      window.location.href = "greenar://app";
                      // Fallback: show message after delay
                      setTimeout(() => {
                        alert(t("loginSuccess.appNotFound") || "Please open the GreenAR app from your device.");
                      }, 1000);
                    }}
                    className="w-full bg-gradient-to-r from-primary to-secondary hover:opacity-90"
                    size="lg"
                  >
                    {t("loginSuccess.openApp") || "Open GreenAR App"}
                  </Button>

                  <Button
                    onClick={() => {
                      auth.signOut();
                      navigate("/");
                    }}
                    variant="outline"
                    className="w-full"
                  >
                    {t("loginSuccess.signOut") || "Sign Out"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
